import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Switch from "@mui/material/Switch"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useMemo, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import { getApiErrorMessage, getTimeZoneOptions } from "./meetingUtils"

export function currentTimeIn(timeZone, now = new Date()) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone,
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    }).format(now)
  } catch (error) {
    return ""
  }
}

export default function MeetingTimeZonesSection() {
  const [zones, setZones] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [label, setLabel] = useState("")
  const [timeZone, setTimeZone] = useState(null)
  const [isAdding, setIsAdding] = useState(false)

  const load = useCallback(() => {
    setIsLoading(true)
    return adminApi
      .fetchMeetingTimeZones()
      .then(({ data }) => setZones(data.results || []))
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not load the time zones.")
        )
      )
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Zones already in the list are not offered again.
  const available = useMemo(() => {
    const taken = new Set(zones.map((zone) => zone.time_zone))
    return getTimeZoneOptions().filter((name) => !taken.has(name))
  }, [zones])

  function handleAdd(event) {
    event.preventDefault()
    setErrorMessage("")
    setIsAdding(true)
    adminApi
      .createMeetingTimeZone({ label, time_zone: timeZone })
      .then(() => {
        setLabel("")
        setTimeZone(null)
        return load()
      })
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not add the time zone.")
        )
      )
      .finally(() => setIsAdding(false))
  }

  function handleToggle(zone) {
    setErrorMessage("")
    adminApi
      .updateMeetingTimeZone(zone.id, { is_active: !zone.is_active })
      .then(load)
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not update the time zone.")
        )
      )
  }

  function handleDelete(zone) {
    setErrorMessage("")
    adminApi
      .deleteMeetingTimeZone(zone.id)
      .then(load)
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not remove the time zone.")
        )
      )
  }

  return (
    <Column gap={2}>
      <Typography variant="body2" color="text.secondary">
        The time zones offered when booking a meeting. A zone you add, or switch
        on or off here, shows in the booking form straight away.
      </Typography>
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <form onSubmit={handleAdd}>
          <Row sx={{ gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
            <TextField
              label="Name"
              size="small"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              inputProps={{ maxLength: 64 }}
              sx={{ minWidth: 200 }}
            />
            <Autocomplete
              size="small"
              options={available}
              value={timeZone}
              onChange={(_, value) => setTimeZone(value)}
              sx={{ minWidth: 280 }}
              renderInput={(params) => (
                <TextField {...params} label="Time zone" />
              )}
            />
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isAdding}
              disabled={!label.trim() || !timeZone}
            >
              Add time zone
            </LoadingButton>
          </Row>
        </form>
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Time zone</TableCell>
              <TableCell>Time there now</TableCell>
              <TableCell>In booking form</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {zones.map((zone) => (
              <TableRow key={zone.id}>
                <TableCell>{zone.label}</TableCell>
                <TableCell>{zone.time_zone}</TableCell>
                <TableCell>{currentTimeIn(zone.time_zone)}</TableCell>
                <TableCell>
                  <Switch
                    checked={zone.is_active}
                    onChange={() => handleToggle(zone)}
                    inputProps={{ "aria-label": `Show ${zone.label}` }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={`Remove ${zone.label}`}
                    onClick={() => handleDelete(zone)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && zones.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No time zones yet. Add one above.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Column>
  )
}
